/**
 * Mega-menu injector — DOM coordination layer.
 *
 * Horizon's @theme-accepted blocks are opaque from the parent section's Liquid:
 * section.blocks returns type "@theme" with empty settings, so the parent can't
 * read a mega-menu block's menu_item_title to know which top-level nav link
 * should host its dropdown. To work around this, each mega-menu block renders
 * its full visible HTML inside an inert <template data-mega-menu-template
 * data-menu-item-title="...">. This script reads those templates, matches them
 * against top-level nav links by title (case-insensitive), and injects the
 * cloned content into the matching list item, wrapped in the markup that
 * Horizon's existing header-menu.js + CSS expect (.menu-list__submenu with
 * ref="submenu[]", aria-haspopup/controls/expanded on the link).
 */

(() => {
  let counter = 0;
  let observer = null;

  function buildTitleMap() {
    const map = new Map();
    document.querySelectorAll('template[data-mega-menu-template]').forEach((tpl) => {
      const title = (tpl.dataset.menuItemTitle || '').trim().toLowerCase();
      if (title && !map.has(title)) map.set(title, tpl);
    });
    return map;
  }

  function injectMegaMenus() {
    const titleToTemplate = buildTitleMap();
    if (titleToTemplate.size === 0) return false;

    const menuItems = document.querySelectorAll('.menu-list__list-item:not([slot="overflow"])');
    if (menuItems.length === 0) return false;

    let didInject = false;

    menuItems.forEach((item) => {
      const link = item.querySelector('.menu-list__link');
      if (!link) return;
      // Skip if a submenu is already present (Liquid-rendered link.links submenu,
      // or our own previous injection that survived).
      if (item.querySelector(':scope > .menu-list__submenu')) return;

      const titleEl = link.querySelector('.menu-list__link-title');
      const title = (titleEl ? titleEl.textContent : link.textContent).trim().toLowerCase();
      const tpl = titleToTemplate.get(title);
      if (!tpl) return;

      counter += 1;
      const submenuId = `mega-menu-injected-submenu-${counter}`;

      // Wire the link with the ARIA attributes header-menu.js + CSS look for.
      link.setAttribute('aria-haspopup', 'true');
      link.setAttribute('aria-controls', submenuId);
      link.setAttribute('aria-expanded', 'false');
      // Mark the link itself so Horizon's section hydration treats it as
      // already-up-to-date (matches the existing data-skip-node-update on
      // .menu-list__link from _header-menu.liquid; just re-asserting here).
      link.setAttribute('data-skip-node-update', 'true');

      // Build .menu-list__submenu > .menu-list__submenu-inner wrapper, matching
      // the structure _header-menu.liquid would have rendered if the parent
      // section could have introspected the block.
      const submenuWrapper = document.createElement('div');
      submenuWrapper.className = 'menu-list__submenu';
      submenuWrapper.setAttribute('ref', 'submenu[]');
      submenuWrapper.setAttribute('data-skip-node-update', 'true');
      submenuWrapper.dataset.megaMenuInjected = '';

      const submenuInner = document.createElement('div');
      submenuInner.className = 'menu-list__submenu-inner';
      submenuInner.id = submenuId;
      submenuInner.appendChild(tpl.content.cloneNode(true));

      submenuWrapper.appendChild(submenuInner);
      item.appendChild(submenuWrapper);
      didInject = true;
    });

    return didInject;
  }

  function injectMobileMegaMenus() {
    const titleToTemplate = buildTitleMap();
    if (titleToTemplate.size === 0) return false;

    const targets = document.querySelectorAll(
      '[data-kubota-mobile-mega-menu]:not([data-mega-menu-mobile-filled="true"])'
    );
    if (targets.length === 0) return false;

    let didInject = false;

    targets.forEach((target) => {
      const title = (target.dataset.kubotaMobileMegaMenu || '').trim().toLowerCase();
      const tpl = titleToTemplate.get(title);
      if (!tpl) return;

      // Clone only the inner card list — not the desktop wrapper / padding.
      // This way mobile CSS scope owns layout entirely.
      const cardList = tpl.content.querySelector('.mega-menu__list--kubota-cards');
      if (!cardList) return;

      target.appendChild(cardList.cloneNode(true));
      target.dataset.megaMenuMobileFilled = 'true';
      didInject = true;
    });

    return didInject;
  }

  function ensureObserver() {
    if (observer) return;
    // Section hydration may replace the menu's DOM children (which blows away
    // our injected submenus) without dispatching an event we can listen for.
    // Watch the header-component subtree and re-inject whenever anything
    // changes. The dedup check at the top of injectMegaMenus prevents loops.
    const root = document.querySelector('header-component') || document.body;

    let pending = false;
    observer = new MutationObserver(() => {
      if (pending) return;
      pending = true;
      // Coalesce multiple mutations into one re-inject pass per tick.
      queueMicrotask(() => {
        pending = false;
        injectMegaMenus();
        injectMobileMegaMenus();
      });
    });

    observer.observe(root, { childList: true, subtree: true });
  }

  function start() {
    injectMegaMenus();
    injectMobileMegaMenus();
    ensureObserver();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
